import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { ASSESSMENT_LEVELS, getLevelLabel } from "../utils/traitUtils";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../../ui/tooltip";

// ─── Types ──────────────────────────────────────────────────────────────────

interface AssessmentLevelInputProps {
  value: number;
  onChange: (level: number) => void;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  showLabels?: boolean;
  accentColor?: string;
  className?: string;
  levelDescriptions?: Record<number, string>;
  loadingDescriptions?: boolean;
}

// ─── Size config ────────────────────────────────────────────────────────────

const SIZE_CONFIG = {
  sm: { circle: "w-6 h-6", text: "text-[10px]", gap: "gap-2", labelGap: "gap-4" },
  md: { circle: "w-8 h-8", text: "text-xs", gap: "gap-3", labelGap: "gap-6" },
  lg: { circle: "w-10 h-10", text: "text-sm", gap: "gap-4", labelGap: "gap-8" },
};

// ─── Level color (Tailwind bg) ──────────────────────────────────────────────

const LEVEL_FILL_COLORS = [
  "bg-gray-300",
  "bg-blue-500",
  "bg-amber-500",
  "bg-emerald-500",
];

const LEVEL_RING_COLORS = [
  "ring-gray-300",
  "ring-blue-300",
  "ring-amber-300",
  "ring-emerald-300",
];

// ─── Component ──────────────────────────────────────────────────────────────

const AssessmentLevelInput: React.FC<AssessmentLevelInputProps> = ({
  value,
  onChange,
  size = "md",
  disabled = false,
  showLabels = false,
  accentColor,
  className,
  levelDescriptions,
  loadingDescriptions,
}) => {
  const { t } = useTranslation();
  const sizeConfig = SIZE_CONFIG[size];

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn("flex flex-col items-start", className)}
        role="radiogroup"
        aria-label={t("trait_assessment_level", "Assessment level")}
      >
        <div className={cn("flex items-start", sizeConfig.gap)}>
          {ASSESSMENT_LEVELS.map((level) => {
            const isActive = value === level.value;
            const label = getLevelLabel(level.value, t);

            return (
              <div key={level.value} className="flex flex-col items-center gap-1.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      aria-label={label}
                      disabled={disabled}
                      onClick={() => {
                        if (!disabled) onChange(level.value);
                      }}
                      className={cn(
                        "rounded-full transition-all duration-200 flex items-center justify-center ring-2 ring-offset-1 shrink-0",
                        sizeConfig.circle,
                        disabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer hover:scale-110 active:scale-95",
                        isActive
                          ? cn(LEVEL_FILL_COLORS[level.value], LEVEL_RING_COLORS[level.value])
                          : "bg-gray-100 ring-transparent hover:ring-gray-200"
                      )}
                    >
                      {isActive && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={6} className="max-w-[220px]">
                    <p className="capitalize font-medium">{label}</p>
                    {levelDescriptions?.[level.value] ? (
                      <p className="text-xs mt-1 font-normal leading-relaxed opacity-90">
                        {levelDescriptions[level.value]}
                      </p>
                    ) : loadingDescriptions && level.value > 0 ? (
                      <p className="text-xs mt-1 font-normal opacity-60">…</p>
                    ) : null}
                  </TooltipContent>
                </Tooltip>
                {showLabels && (
                  <span
                    className={cn(
                      "text-center capitalize whitespace-nowrap",
                      sizeConfig.text,
                      isActive ? level.color : "text-gray-300"
                    )}
                  >
                    {label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default AssessmentLevelInput;
